import apiClient from './api';

const MASTERS = {
  countries: 'countries',
  currencies: 'currencies',
  languages: 'languages',
  companyTypes: 'company-types',
  sectionTypes: 'section-types',
  menuSections: 'sections',
  dishSizes: 'dish-sizes',
  dishUnits: 'dish-units',
  dishTags: 'dish-tags',
};

// The Django users app is mounted at /api/users/, so keep every path consistent with the other services.
const url = (kind, id) => `/users/masters/${MASTERS[kind]}/${id ? `${id}/` : ''}`;

export const mastersService = {
  list: async (kind, { includeInactive = false } = {}) => {
    const { data } = await apiClient.get(url(kind), {
      // _t busts the browser cache without a Cache-Control header, which would force an unsupported CORS preflight.
      params: {
        ...(includeInactive ? { include_inactive: 'true' } : {}),
        _t: Date.now(),
      },
    });
    return data?.results || [];
  },
  create: async (kind, payload) => {
    const { data } = await apiClient.post(url(kind), payload);
    return data?.data;
  },
  update: async (kind, id, payload) => {
    const { data } = await apiClient.put(url(kind, id), payload);
    return data?.data;
  },
  remove: async (kind, id) => {
    const { data } = await apiClient.delete(url(kind, id));
    return data;
  },
  timezones: async () => {
    const { data } = await apiClient.get('/users/masters/timezones/');
    return data?.results || [];
  },
  /** Bundled refresh: one round-trip for several master lists, keyed by their raw URL slugs. */
  fetchBundle: async (kinds, { includeInactive = false } = {}) => {
    const slugs = (kinds || []).map((k) => MASTERS[k] || k);
    const { data } = await apiClient.get('/users/masters/bundle/', {
      params: {
        ...(slugs.length ? { kinds: slugs.join(',') } : {}),
        ...(includeInactive ? { include_inactive: 'true' } : {}),
        _t: Date.now(),
      },
    });
    return data?.data || {};
  },
};

export const appSettingsService = {
  get: async () => {
    const { data } = await apiClient.get('/users/settings/app/');
    return data?.data;
  },
  update: async (patch) => {
    const { data } = await apiClient.put('/users/settings/app/', patch);
    return data?.data;
  },
};

/** Dish-master facade over /api/users/dish/masters/<type>/; ingredient is per-restaurant, so it needs an id. */
// Masters with an image post multipart when the payload carries a File; everything else stays JSON.
const dishMasterBody = (payload) => {
  const hasFile = payload && Object.values(payload).some((v) => v instanceof File);
  if (!hasFile) return [payload, {}];
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  return [fd, { headers: { 'Content-Type': 'multipart/form-data' } }];
};

export const dishMastersService = {
  /** Bundled refresh returning several dish-master lists keyed by type in ONE round-trip. */
  refreshBundle: async (kinds, { restaurantId, includeInactive = false } = {}) => {
    const { data } = await apiClient.get('/users/dish/masters/bundle/', {
      params: {
        _t: Date.now(),
        ...(Array.isArray(kinds) && kinds.length ? { kinds: kinds.join(',') } : {}),
        ...(restaurantId ? { restaurant_id: restaurantId } : {}),
        ...(includeInactive ? { include_inactive: 'true' } : {}),
      },
    });
    return data?.data || {};
  },
  refresh: async (type, { restaurantId, includeInactive = false } = {}) => {
    const { data } = await apiClient.get(`/users/dish/masters/${type}/`, {
      params: {
        _t: Date.now(),
        ...(restaurantId ? { restaurant_id: restaurantId } : {}),
        ...(includeInactive ? { include_inactive: 'true' } : {}),
      },
    });
    return Array.isArray(data?.data) ? data.data : [];
  },
  create: async (type, payload) => {
    const [body, config] = dishMasterBody(payload);
    const { data } = await apiClient.post(`/users/dish/masters/${type}/`, body, config);
    return data?.data;
  },
  update: async (type, id, payload) => {
    const [body, config] = dishMasterBody(payload);
    const { data } = await apiClient.put(`/users/dish/masters/${type}/${id}/`, body, config);
    return data?.data;
  },
  // Delete a master row: a soft delete by default, or { hard: true } to remove it permanently.
  remove: async (type, id, { hard = false } = {}) => {
    const { data } = await apiClient.delete(`/users/dish/masters/${type}/${id}/`, {
      params: hard ? { hard: 'true' } : {},
    });
    return data;
  },
};

export const MASTER_KINDS = MASTERS;
